<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import ChatIcon from '@/components/base/assets/ChatIcon.vue';
import ChevronIcon from '@/components/base/assets/ChevronIcon.vue';
import Container from '@/components/base/layout/Container.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import CampaignCarousel from '@/components/layout/header/CampaignCarousel.vue';
import { useRouterLinks } from '@/composables/navigation/useRouterLinks';
import { useDelivery } from '@/composables/useDelivery';
import { useReferralElement } from '@/composables/useReferralElement';
import { useChat } from '@/stores/chat';
import { useOverlay } from '@/stores/overlay';
import { GlobalCampaignCarousel } from '@/utils/cms';

defineProps<{
  campaigns?: GlobalCampaignCarousel['items'];
  isCarouselLoading: boolean;
}>();

defineEmits<{
  toggleShippingDetails: [];
}>();

const router = useRouter();
const store = useStore();
const { freeShippingThreshold } = useDelivery(store);

const chat = useChat();

const referralLink = ref<HTMLElement>();
const topShelfLinks = ref<HTMLElement>();

const getDataPromo = (name: string) => ({
  'data-promo': '1',
  'data-promo-creative': 'Header Links',
  'data-promo-name': `Utility Links: ${name}`,
});

onMounted(chat.init);

const { showReferralElement } = useReferralElement(referralLink, 'global_header_redesignv1');

useRouterLinks(topShelfLinks, router);

const backgroundColor = 'bg-nuts-neutral-950';
const foregroundColor = 'text-white';

const linksContainer = useTemplateRef('linksContainer');
useRouterLinks(linksContainer, useRouter());

const helpRef = useTemplateRef<HTMLElement>('helpRef');
const { activate, deactivate } = useFocusTrap(helpRef);
const overlay = useOverlay();
const showHelp = ref(false);
watch(showHelp, (value) => {
  if (value) {
    overlay.show(true);
    activate();
  } else {
    overlay.hide();
    deactivate();
  }
});
</script>

<template>
  <div :class="backgroundColor" data-test="top-shelf">
    <Container tag="nav" aria-label="Top shelf">
      <div class="flex items-center justify-between py-1" ref="topShelfLinks">
        <CampaignCarousel :items="campaigns" :isLoading="isCarouselLoading || chat.pending">
          <a href="/bestsellers/">
            <SmallBodyText> Discover our Best-Selling Favorites </SmallBodyText>
          </a>
        </CampaignCarousel>

        <ul class="inline-flex items-center ml-auto gap-x-6" data-test="persistent-nav">
          <li v-if="showReferralElement" data-test="referral-link">
            <a
              href="/refer"
              class="top-shelf-link py-0.5"
              ref="referralLink"
              v-bind="getDataPromo('Refer & Earn')"
            >
              <SmallBodyText class="hover:underline" :class="foregroundColor">
                Refer &amp; Get $20
              </SmallBodyText>
            </a>
          </li>

          <li data-test="nuts-for-business">
            <a class="py-0.5" href="/wholesale/" v-bind="getDataPromo('Nuts for Business')">
              <SmallBodyText class="hover:underline" :class="foregroundColor">
                Nuts For Business
              </SmallBodyText>
            </a>
          </li>

          <li data-test="help">
            <PopperWrapper
              arrow
              class="legacy-max-width"
              ref="helpRef"
              :show="showHelp"
              @keyup.escape="showHelp = false"
              @mouseenter="showHelp = true"
              @mouseleave="showHelp = false"
            >
              <UnstyledButton
                class="flex items-center pr-4 border-r border-solid border-nuts-neutral-700"
                @click="showHelp = !showHelp"
              >
                <SmallBodyText class="hover:underline" :class="foregroundColor">
                  Help
                </SmallBodyText>
                <ChevronIcon direction="down" :size="16" :class="foregroundColor" />
              </UnstyledButton>
              <template #content>
                <div
                  class="flex flex-col items-center gap-2 px-6 py-8 border-b border-solid border-neutral-300 min-w-64"
                >
                  <BaseBodyText> We are here to help </BaseBodyText>
                  <div class="flex flex-col items-center gap-3">
                    <UnstyledButton
                      @click="
                        chat.open();
                        showHelp = false;
                      "
                    >
                      <LargeBodyText class="flex items-center self-center gap-2">
                        <ChatIcon />
                        <span class="underline"> Chat with us </span>
                      </LargeBodyText>
                    </UnstyledButton>
                    <SmallBodyText v-if="chat.available" class="text-nuts-lime-900">
                      Typically respond within 3 minutes
                    </SmallBodyText>
                  </div>
                </div>
                <ul
                  class="flex my-4 divide-x-2 divide-solid divide-neutral-300"
                  ref="linksContainer"
                >
                  <li class="flex justify-center w-1/2">
                    <a class="hover:underline" href="/contact" @click="showHelp = false">
                      <BaseBodyText class="text-black">Contact us</BaseBodyText>
                    </a>
                  </li>
                  <li class="flex justify-center w-1/2">
                    <a class="hover:underline" href="/help" @click="showHelp = false">
                      <BaseBodyText class="text-black">Help Center</BaseBodyText>
                    </a>
                  </li>
                </ul>
              </template>
            </PopperWrapper>
          </li>

          <li>
            <UnstyledButton
              aria-label="shipping details"
              class="inline-flex items-center gap-x-1"
              @click="$emit('toggleShippingDetails')"
            >
              <SmallBodyText
                class="py-1 font-semibold leading-5 lg:font-normal whitespace-nowrap"
                :class="foregroundColor"
              >
                FREE shipping on orders over ${{ dollars(freeShippingThreshold) }}!
              </SmallBodyText>
            </UnstyledButton>
          </li>
        </ul>
      </div>
    </Container>
  </div>
</template>

<style scoped>
.top-shelf-link {
  @apply text-white;
}
</style>
